import * as Session from '../../domain/Session';
import * as Pager from '../../domain/Pager';
import * as Topic from '../../domain/Topic';
import RepositoryFactory from '../../repositories/RepositoryFactory';
import TopicRepository from '../../repositories/TopicRepository';
import { actionCreators as detailActionCreators, Action as DetailAction } from './detail';
import { actionCreators as listActionCreators, Action as ListAction } from './list';

export default class TopicDetailActionDispatcher {
  private dispatch: (action: DetailAction | ListAction) => DetailAction | ListAction;
  private repository: TopicRepository;

  public constructor(dispatch: (action: DetailAction | ListAction) => DetailAction | ListAction, session: Session.Value) {
    this.dispatch = dispatch;
    this.repository = RepositoryFactory.createTopicRepository(session);

    this.fetchList = this.fetchList.bind(this);
    this.fetch = this.fetch.bind(this);
    this.disablePostComment = this.disablePostComment.bind(this);
    this.sendHeart = this.sendHeart.bind(this);
    this.toggleClip = this.toggleClip.bind(this);
  }

  /**
   * トピックのリストを取得して表示する
   *
   * @param {Pager.Value}
   * @returns {Promise<Topic.EntityList>}
   */
  public async fetchList(pager: Pager.Value): Promise<Topic.EntityList> {
    this.dispatch(listActionCreators.requestFetchTopicList(pager));
    const entityList = await this.repository.findAll(pager);
    this.dispatch(listActionCreators.doneFetchTopicList(entityList));

    return entityList;
  }

  /**
   * トピック詳細を取得して表示する
   *
   * @param {number} topicId
   * @returns {Promise<Topic.Entity>}
   */
  public async fetch(topicId: number): Promise<Topic.Entity> {
    const entity = await this.repository.find(topicId);
    this.dispatch(detailActionCreators.doneFetchTopic(entity));

    if (entity.isEmpty()) {
      return entity;
    }

    // 詳細が取得できたらアクセス数をカウントアップさせる
    this.repository.storeAccess(topicId);
    return entity;
  }

  /**
   * コメント受付を終了する
   *
   * @param {Topic.Entity} entity
   */
  public async disablePostComment(entity: Topic.Entity) {
    const updatedEntity = await this.repository.updateDisabledPostComment(entity);

    this.dispatch(detailActionCreators.doneDisabledPostComment(updatedEntity));
  }

  /**
   * トピックにハートを送る
   *
   * @param {Topic.Entity} entity
   * @returns {Promise<void>}
   */
  public async sendHeart(entity: Topic.Entity): Promise<void> {
    const updatedEntity = await this.repository.storeHeart(entity);

    this.dispatch(detailActionCreators.doneSendTopicHeart(updatedEntity));
  }

  /**
   * トピックをクリップしたり解除したりする
   *
   * @param {Topic.Entity} entity
   * @returns {Promise<vlid>}
   */
  public toggleClip(entity: Topic.Entity): Promise<void> {
    if (entity.isClipped()) {
      return this.removeClippedTopic(entity);
    }

    return this.clipTopic(entity);
  }

  /**
   * トピックをクリップする
   *
   * @param {Topic.Entity} entity
   * @returns {Promise<vlid>}
   */
  private async clipTopic(entity: Topic.Entity): Promise<void> {
    const updatedEntity = await this.repository.storeClip(entity);

    this.dispatch(detailActionCreators.doneToggleClip(updatedEntity));
  }

  /**
   * トピックのクリップを解除する
   *
   * @param {Topic.Entity} entity
   * @returns {Promise<void>}
   */
  private async removeClippedTopic(entity: Topic.Entity): Promise<void> {
    const updatedEntity = await this.repository.destroyClip(entity);

    this.dispatch(detailActionCreators.doneToggleClip(updatedEntity));
  }
}
